/**
 * Help System — shared markdown primitive
 *
 * A small, dependency-free markdown → HTML renderer scoped to the subset of
 * markdown used by first-party help content. It escapes all raw HTML by
 * construction (no passthrough), so the output is safe to bind via [innerHTML];
 * Angular's DomSanitizer is still applied downstream as defence in depth.
 *
 * Supported: frontmatter, h1–h4, paragraphs, bold, italic, inline code,
 * fenced code, unordered/ordered lists, tables, blockquotes, horizontal rules,
 * external links, internal topic links ([text](topic/id) with no scheme), and
 * ::: callouts (warning | info | tip).
 *
 * This is the single rendering/parsing primitive shared by BOTH the artist and
 * gallery help systems.
 */

import { ParsedHelpDoc } from './help.types';

const FRONTMATTER_RE = /^---\n([\s\S]*?)\n---\n?/;

/** Split frontmatter from body and extract the fields the UI needs. */
export function parseHelpDoc(raw: string): ParsedHelpDoc {
  let body = raw;
  const meta: Record<string, string> = {};
  const related: string[] = [];

  const match = raw.match(FRONTMATTER_RE);
  if (match) {
    body = raw.slice(match[0].length);
    const lines = match[1].split('\n');
    let collectingRelated = false;
    for (const line of lines) {
      if (collectingRelated) {
        const item = line.match(/^\s*-\s+(.*)$/);
        if (item) {
          related.push(stripQuotes(item[1].trim()));
          continue;
        }
        collectingRelated = false;
      }
      const kv = line.match(/^(\w+):\s*(.*)$/);
      if (!kv) continue;
      const key = kv[1];
      const value = kv[2].trim();
      if (key === 'relatedTopics' && value === '') {
        collectingRelated = true;
      } else if (key === 'relatedTopics' && value.startsWith('[')) {
        value
          .slice(1, -1)
          .split(',')
          .map(s => stripQuotes(s.trim()))
          .filter(Boolean)
          .forEach(t => related.push(t));
      } else {
        meta[key] = stripQuotes(value);
      }
    }
  }

  return {
    title: meta['title'] || '',
    description: meta['description'] || '',
    relatedTopics: related,
    body,
  };
}

function stripQuotes(v: string): string {
  if ((v.startsWith('"') && v.endsWith('"')) || (v.startsWith("'") && v.endsWith("'"))) {
    return v.slice(1, -1);
  }
  return v;
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Inline formatting: code, bold, italic, links. Operates on escaped text. */
function renderInline(text: string): string {
  let out = escapeHtml(text);

  // inline code first so its contents are not further formatted
  out = out.replace(/`([^`]+)`/g, (_m, code) => `<code>${code}</code>`);
  // bold then italic
  out = out.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  out = out.replace(/(^|[^*])\*([^*]+)\*/g, '$1<em>$2</em>');

  // links: [text](target). Internal (no scheme) → data-help-topic for in-drawer nav.
  out = out.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_m, label, href) => {
    const isExternal = /^(https?:)?\/\//.test(href) || href.startsWith('mailto:');
    if (isExternal) {
      return `<a href="${href}" target="_blank" rel="noopener noreferrer">${label}</a>`;
    }
    return `<a href="#" data-help-topic="${href}">${label}</a>`;
  });

  return out;
}

const CALLOUT_KINDS = new Set(['warning', 'info', 'tip', 'note']);

/** Render a markdown body to safe HTML. */
export function renderMarkdown(markdown: string): string {
  const lines = markdown.replace(/\r\n/g, '\n').split('\n');
  const html: string[] = [];

  let i = 0;
  while (i < lines.length) {
    let line = lines[i];

    // blank
    if (line.trim() === '') { i++; continue; }

    // fenced code
    if (line.trim().startsWith('```')) {
      const code: string[] = [];
      i++;
      while (i < lines.length && !lines[i].trim().startsWith('```')) {
        code.push(escapeHtml(lines[i]));
        i++;
      }
      i++; // closing fence
      html.push(`<pre><code>${code.join('\n')}</code></pre>`);
      continue;
    }

    // callout  :::warning ... :::
    const callout = line.trim().match(/^:::(\w+)(?:\s+(.*))?$/);
    if (callout && CALLOUT_KINDS.has(callout[1])) {
      const kind = callout[1];
      const heading = callout[2] ? callout[2].trim() : '';
      const inner: string[] = [];
      i++;
      while (i < lines.length && lines[i].trim() !== ':::') {
        inner.push(lines[i]);
        i++;
      }
      i++; // closing :::
      const headingHtml = heading ? `<div class="help-callout__title">${renderInline(heading)}</div>` : '';
      html.push(
        `<div class="help-callout help-callout--${kind}">${headingHtml}${renderMarkdown(inner.join('\n'))}</div>`,
      );
      continue;
    }

    // headings
    const heading = line.match(/^(#{1,4})\s+(.*)$/);
    if (heading) {
      const level = heading[1].length;
      html.push(`<h${level}>${renderInline(heading[2])}</h${level}>`);
      i++;
      continue;
    }

    // horizontal rule
    if (/^---+$/.test(line.trim())) { html.push('<hr />'); i++; continue; }

    // table  | a | b |  with a separator row underneath
    if (line.trim().startsWith('|') && i + 1 < lines.length && /^\s*\|?[\s:|-]+\|?\s*$/.test(lines[i + 1])) {
      const header = splitRow(line);
      i += 2; // header + separator
      const bodyRows: string[][] = [];
      while (i < lines.length && lines[i].trim().startsWith('|')) {
        bodyRows.push(splitRow(lines[i]));
        i++;
      }
      const thead = `<thead><tr>${header.map(c => `<th>${renderInline(c)}</th>`).join('')}</tr></thead>`;
      const tbody = `<tbody>${bodyRows
        .map(r => `<tr>${r.map(c => `<td>${renderInline(c)}</td>`).join('')}</tr>`)
        .join('')}</tbody>`;
      html.push(`<table class="help-table">${thead}${tbody}</table>`);
      continue;
    }

    // blockquote
    if (line.trim().startsWith('>')) {
      const quote: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith('>')) {
        quote.push(lines[i].replace(/^\s*>\s?/, ''));
        i++;
      }
      html.push(`<blockquote>${renderMarkdown(quote.join('\n'))}</blockquote>`);
      continue;
    }

    // unordered list
    if (/^\s*[-*]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
        items.push(`<li>${renderInline(lines[i].replace(/^\s*[-*]\s+/, ''))}</li>`);
        i++;
      }
      html.push(`<ul>${items.join('')}</ul>`);
      continue;
    }

    // ordered list
    if (/^\s*\d+\.\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        items.push(`<li>${renderInline(lines[i].replace(/^\s*\d+\.\s+/, ''))}</li>`);
        i++;
      }
      html.push(`<ol>${items.join('')}</ol>`);
      continue;
    }

    // paragraph (gather consecutive non-blank, non-structural lines)
    const para: string[] = [line];
    i++;
    while (
      i < lines.length &&
      lines[i].trim() !== '' &&
      !/^(#{1,4}\s|>|\s*[-*]\s|\s*\d+\.\s|\|)/.test(lines[i]) &&
      !lines[i].trim().startsWith('```') &&
      !lines[i].trim().startsWith(':::')
    ) {
      para.push(lines[i]);
      i++;
    }
    html.push(`<p>${renderInline(para.join(' '))}</p>`);
  }

  return html.join('\n');
}

function splitRow(line: string): string[] {
  return line
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map(c => c.trim());
}
